/**
 * Тренер: что сказал бы личный тренер в этот момент. Решение владельца от
 * 30 октября 2026 — вместо «слов ученика со старта» (решение 17) на экране
 * возвращения и чаще: «в ситуациях, где обычно бы личный тренер сказал что-
 * нибудь». Плюс цитаты известных людей — только проверенные (см. QUOTES).
 *
 * **Чем тренер отличается от совета дня** (src/lib/advice-lines.ts). Совет —
 * о методе, один на день, из всех ста. Тренер — о деле и о том, зачем оно,
 * и говорит по ситуации: после серии ошибок, перед задачей формата экзамена,
 * в конце дня, в итоге недели, при отставании от плана, ближе к экзамену.
 *
 * **Риск, названный владельцу и принятый им.** Плакатная фраза через неделю
 * становится обоями — так уже было с постоянными подписями на «Сегодня».
 * Поэтому строка привязана к ситуации и, где можно, несёт настоящее число
 * ({days}, {need}…): «5 дней из 7» звучит лично, «верь в себя» — нет.
 *
 * Тон — тот же, что везде: человека не оценивать, без «Молодец», без «!».
 * Тренер говорит о деле, о цене и о следующем шаге. Держит
 * tests/coach-lines.test.ts.
 */

/** Ситуация, в которой тренер что-то говорит. */
import { plural } from "./dates";

export type CoachSituation =
  | "return" //       перерыв от четырёх дней, экран «Возвращение»
  | "missStreak" //   две ошибки подряд в наборе
  | "challenge" //    перед задачей формата экзамена
  | "newSkill" //     первая задача нового навыка
  | "dayDone" //      набор закрыт
  | "hardDay" //      набор закрыт, верных меньше половины
  | "weekUp" //       неделя не хуже прошлой
  | "weekDown" //     неделя слабее прошлой или пустая
  | "behind" //       темп ниже нужного для всей карты
  | "examNear" //     до экзамена меньше 60 дней
  | "persist"; //     без повода — о настойчивости вообще

/** Длина под две строки карточки при 15px. Чуть длиннее совета: есть подпись. */
export const COACH_LIMIT = 140;

/**
 * Строки с подстановками. `{days}` — дней перерыва или занятий, `{need}` —
 * навыков в неделю, `{weeks}` — недель до экзамена, `{left}` — дней до экзамена.
 * Слово при числе — `{days|день|дня|дней}`: оно склоняется по тому же числу.
 * Зашитое слово дало бы «21 дней» и «1 дней» (spec-guard, 30 октября 2026).
 */
export const COACH: Record<CoachSituation, readonly string[]> = {
  return: [
    "Перерыв — не провал. Провал — не вернуться. Сегодня коротко, но сегодня.",
    "Форма уходит быстрее знаний. Первые десять минут будут туже обычного — это нормально.",
    "{days} {days|день|дня|дней} без задач стоят ровно столько, не больше. Вчерашнее не догоняют — продолжают с сегодняшнего.",
    "Самое трудное в возвращении — первая задача. Вторая уже просто задача.",
    "Начни с того, что начало забываться. Через десять минут станет ясно, сколько удержалось.",
    "Пропуск ничего не сжёг: план растянулся на оставшиеся недели. Дальше — по одной задаче.",
  ],
  missStreak: [
    "Две ошибки подряд — сигнал, а не приговор. Остановись и перечитай условие медленно.",
    "Когда не идёт, не ускоряйся. Медленнее — значит точнее.",
    "Ошибки сейчас дешевле, чем на экзамене. Каждая из них — подсказка, где именно дыра.",
    "Сделай паузу на минуту. Свежий взгляд находит то, что упрямство пропускает.",
    "Не спеши перерешивать. Сначала найди шаг, где свернул не туда.",
    "Трудно — значит, задачи на твоём уровне. На лёгких никто не растёт.",
  ],
  challenge: [
    "Задача формата экзамена. Решай так, как на экзамене: условие, план, проверка.",
    "Здесь проверяется не тема, а умение собрать несколько тем в одно решение.",
    "Не узнал тип задачи — это нормально. Узнай, что дано и что спрашивают, остальное следует.",
    "Такие задачи и решают экзамен. Каждая решённая сейчас — минус одна неожиданность на нём.",
  ],
  newSkill: [
    "Новый навык. Первые три задачи — разведка, а не экзамен: ошибаться здесь можно.",
    "Новое всегда кажется сложнее, чем есть. Через неделю повторений оно станет обычным.",
    "Не понял правило с первого раза — открой разбор. Его для того и написали.",
    "Новая тема держится на старых. Если споткнулся, проблема часто на ступень ниже.",
  ],
  dayDone: [
    "Набор закрыт. Завтра часть этих задач вернётся повторением — так память и строится.",
    "День сделан. Регулярность выигрывает у рывков: завтра столько же.",
    "Ещё один день в копилку. Экзамен сдаёт тот, кто набрал таких дней больше.",
    "На сегодня план выполнен. Хочешь больше — добавка есть, но и отдых входит в тренировку.",
    "Закрыто. Лучшее, что можно сделать дальше, — прийти завтра.",
  ],
  hardDay: [
    "Трудный день — тоже день. Половина успеха в том, что ты его не пропустил.",
    "Сегодня ошибок было много. Это не про способности — это про то, что осталось выучить.",
    "Тяжёлые дни учат больше лёгких. Завтра эти же темы вернутся, и пойдут легче.",
    "Не все дни будут получаться. Считается не каждый день, а сумма недель.",
  ],
  // Неделя — ПРОШЛАЯ, мерка — обязательство ученика (src/ui/WeekCard.tsx):
  // строки не сравнивают с позапрошлой неделей, потому что её никто не считал.
  weekUp: [
    "{days} {days|день|дня|дней} занятий на прошлой неделе. Продержи этот ритм ещё три недели — и он перестанет требовать усилий.",
    "Прошлая неделя прошла по плану. Ритм важнее рекорда: держи его, а не превосходи.",
    "{days} {days|день|дня|дней} занятий за неделю. Так и выглядит подготовка — без подвигов, по плану.",
    "Привычка складывается из обычных недель вроде прошлой.",
  ],
  weekDown: [
    "Прошлая неделя вышла короче обещанного. Не догоняй — просто начни эту вовремя.",
    "Один слабый отрезок ничего не решает. Решает, что будет после него.",
    "Если неделя развалилась, выбери свои дни и время — и держись только их.",
    "Меньше занятий — не повод бросить, а повод упростить: пусть будет восемь минут, но в свои дни.",
  ],
  behind: [
    "Чтобы успеть всё к экзамену, нужно {need} {need|навык|навыка|навыков} в неделю. Каждый день занятий — шаг к этому числу.",
    "{weeks} {weeks|неделя|недели|недель} до экзамена. Это много, если начать сейчас, и мало, если начать потом.",
    "Догонять план рывком не нужно. Достаточно одного лишнего дня занятий в неделю.",
    "Темп ниже нужного — это не вина, а данные. Сдвинь одну привычку, и число поменяется.",
  ],
  examNear: [
    "До экзамена {left} {left|день|дня|дней}. Сейчас решает не новое, а то, что ты достаёшь из памяти за минуту.",
    "Последние недели — время повторения. Новое берут, только если старое держится.",
    "Экзамен проверяет то, что ты делал каждый день. Добавлять подвиги поздно, держать ритм — нет.",
    "Спокойствие на экзамене тренируется так же, как задачи: решай на время, в тишине.",
  ],
  persist: [
    "Мотивация приходит после начала, а не до. Открой первую задачу — остальное догонит.",
    "Способности дают старт. До финиша доводит число решённых задач.",
    "Сильнее всех не тот, кто ни разу не ошибся, а тот, кто ошибался и продолжал.",
    "Цель большая, шаг маленький. Сегодня от тебя нужен только шаг.",
    "Скучные дни тоже считаются. Именно они отличают подготовку от порыва.",
    "Знание, добытое своей головой, держится дольше подсмотренного. Поэтому сначала — сам.",
    "Экзамен далеко, задача рядом. Решай ту, что рядом.",
  ],
};

/**
 * Цитата с проверенной подписью. Заполняется только после проверки
 * подлинности (агент, 30 октября 2026): неверно приписанная цитата хуже,
 * чем никакая, — подросток рано или поздно проверит её в поиске.
 */
export type CoachQuote = {
  text: string;
  author: string;
  /** Откуда — книга, письмо, интервью. Ученику не показывается, хранится для проверки. */
  source: string;
  situations: readonly CoachSituation[];
};

/**
 * Проверено агентом 30 октября 2026 по первоисточникам и Wikiquote с
 * источниками, Quote Investigator, Barry Popik. Подписи и формулировки — те,
 * что прошли проверку: где популярная версия расходится с источником, стоит
 * источник (Пойа, Эдисон, Али), где автор другой — другой (Дюрант, а не
 * Аристотель; мантра бегунов у Мураками). Отклонены как неверно приписанные:
 * Ломоносов «Математику уже затем учить следует…», Кюри «В жизни нечего
 * бояться…», Эйнштейн «…дольше остаюсь с задачей», Черчилль «Успех не
 * окончателен…», Конфуций «Неважно, как медленно ты идёшь…». Не вернуть их
 * сюда — они выглядят знакомо именно потому, что их везде приписывают неверно.
 */
export const QUOTES: readonly CoachQuote[] = [
  {
    text: "В геометрии нет царской дороги.",
    author: "Евклид — царю Птолемею, по преданию у Прокла",
    source: "Прокл, «Комментарий к первой книге „Начал“ Евклида», пролог",
    situations: ["persist", "behind"],
  },
  {
    text: "Если не удается решить данную задачу, попытайтесь сначала решить сходную. Нельзя ли придумать более доступную сходную задачу?",
    author: "Дьёрдь Пойа, «Как решать задачу»",
    source: "Пойа, «Как решать задачу», Учпедгиз, 1959 (How to Solve It, 1945)",
    situations: ["missStreak", "challenge"],
  },
  {
    text: "Мы не потому не осмеливаемся, что трудно, — трудно оттого, что мы не осмеливаемся.",
    author: "Сенека, «Нравственные письма к Луцилию»",
    source: "Epistulae morales 104.26, пер. С. А. Ошерова, «Наука», 1977",
    situations: ["challenge", "newSkill", "return"],
  },
  {
    text: "Мы — то, что мы делаем постоянно. Совершенство, таким образом, — не поступок, а привычка.",
    author: "Уилл Дюрант, пересказывая Аристотеля",
    source: "W. Durant, The Story of Philosophy, 1926, гл. об Аристотеле, с. 87",
    situations: ["weekUp", "dayDone"],
  },
  {
    text: "Я промахнулся больше 9000 раз за карьеру. Проиграл почти 300 матчей. Я проваливался снова и снова. Именно поэтому я добиваюсь успеха.",
    author: "Майкл Джордан",
    source: "рекламный ролик Nike «Failure», 1997; Goldman & Papson, Nike Culture, 1998, с. 49",
    situations: ["hardDay", "missStreak"],
  },
  {
    text: "Pauca sed matura — немного, но зрело.",
    author: "Девиз Карла Фридриха Гаусса",
    source: "Sartorius von Waltershausen, Gauss zum Gedächtniss, 1856",
    situations: ["dayDone", "examNear"],
  },
  {
    text: "Я ненавидел каждую минуту. Но говорил себе: «Потерпи сейчас — и проживёшь остаток жизни чемпионом».",
    author: "Мухаммед Али, о подготовке к бою, 1978",
    source: "Newsweek, 25.09.1978, Axthelm & Bonventre, «Ali: Born Again»",
    situations: ["hardDay", "weekDown", "behind"],
  },
  {
    text: "Логикой доказывают, интуицией изобретают.",
    author: "Анри Пуанкаре, «Наука и метод»",
    source: "Science et méthode, 1908, кн. II, гл. II",
    situations: ["challenge", "newSkill"],
  },
  {
    text: "Боль неизбежна. Страдание — личный выбор каждого.",
    author: "Мантра бегунов; приводит Харуки Мураками",
    source: "Мураками, «О чём я говорю, когда говорю о беге», 2007, предисловие; пер. А. Кунина",
    situations: ["hardDay", "return"],
  },
  {
    text: "Результаты? У меня масса результатов: я знаю несколько тысяч вещей, которые не работают.",
    author: "Томас Эдисон, в пересказе сотрудника",
    source: "Dyer & Martin, Edison: His Life and Inventions, 1910, т. 2, гл. 24, с. 615–616",
    situations: ["missStreak", "hardDay"],
  },
  {
    text: "Знание только тогда знание, когда оно приобретено усилиями своей мысли, а не памятью.",
    author: "Лев Толстой, «Круг чтения»",
    source: "«Круг чтения», 9 января; ПСС, т. 41, 1957",
    situations: ["newSkill", "persist"],
  },
  {
    text: "…никакие способности не помогут без увлечения своим делом, без систематической повседневной работы.",
    author: "Андрей Колмогоров, «О профессии математика»",
    source: "Колмогоров, «О профессии математика», 3-е изд., МГУ, 1959, с. 11",
    situations: ["weekUp", "behind", "persist"],
  },
  {
    text: "Необходимость специальных способностей для изучения и понимания математики часто преувеличивают.",
    author: "Андрей Колмогоров, «О профессии математика»",
    source: "Колмогоров, «О профессии математика», 3-е изд., МГУ, 1959, с. 8",
    situations: ["hardDay", "persist"],
  },
  {
    text: "Строя дома, становятся зодчими, а играя на кифаре — кифаристами.",
    author: "Аристотель, «Никомахова этика»",
    source: "Никомахова этика II.1, 1103a32–34, пер. Н. Брагинской",
    situations: ["persist", "dayDone"],
  },
  {
    text: "Крупное научное открытие дает решение крупной проблемы, но и в решении любой задачи присутствует крупица открытия.",
    author: "Дьёрдь Пойа, «Как решать задачу»",
    source: "How to Solve It, 1945, предисловие; пер. 1959",
    situations: ["challenge", "dayDone"],
  },
  {
    text: "Единственный способ научиться математике — заниматься математикой.",
    author: "Пол Халмош, математик",
    source: "Halmos, A Hilbert Space Problem Book, 2-е изд., 1982, предисловие, с. vii",
    situations: ["persist", "return", "weekDown"],
  },
  {
    text: "Первый принцип — не обманывать самого себя. А себя обмануть легче всего.",
    author: "Ричард Фейнман",
    source: "«Cargo Cult Science», речь в Калтехе, 1974; Engineering & Science 37:7",
    situations: ["missStreak", "examNear"],
  },
  {
    text: "Жизнь нелегка ни для кого из нас. Ну и что? Нужно упорство и, главное, уверенность в себе.",
    author: "Мария Склодовская-Кюри, из письма брату, 1894",
    source: "письмо брату от 18.03.1894; Ève Curie, Madame Curie, 1937–38",
    situations: ["hardDay", "return", "behind"],
  },
  {
    text: "Никогда не замечаешь того, что уже сделано; видишь только то, что ещё осталось сделать.",
    author: "Мария Склодовская-Кюри, из письма брату, 1894",
    source: "письмо брату от 18.03.1894; Ève Curie, Madame Curie, 1937–38",
    situations: ["behind", "weekDown"],
  },
];

export type CoachLine = { text: string; author: string | null };

/**
 * Строка тренера для ситуации. Детерминированно от `seed` (ключ ученика и
 * дата): в пределах дня строка не скачет при перерисовке. Каждая третья
 * реплика — цитата, если для ситуации она есть: цитата весомее, но десять
 * цитат подряд читаются как сборник афоризмов, а не как тренер.
 */
export function coachLine(
  situation: CoachSituation,
  vars: Partial<Record<"days" | "need" | "weeks" | "left", number>> = {},
  seed = 0,
): CoachLine {
  const quotes = QUOTES.filter((q) => q.situations.includes(situation));
  const n = Math.abs(Math.floor(seed));
  if (quotes.length > 0 && n % 3 === 2) {
    const q = quotes[Math.floor(n / 3) % quotes.length]!;
    return { text: q.text, author: q.author };
  }
  // Строка с подстановкой, которой не дали числа, не берётся: «{days} дней»
  // на экране хуже, чем другая строка.
  const lines = COACH[situation].filter((l) =>
    [...l.matchAll(/\{(\w+)[|}]/g)].every((m) => vars[m[1] as keyof typeof vars] !== undefined),
  );
  const pool = lines.length > 0 ? lines : COACH.persist;
  // Номер строки — по счёту дней, когда выпала не цитата (n % 3 ∈ {0, 1}).
  // Просто `n % pool.length` вместе с правилом цитаты навсегда прятал строки
  // с номерами 2 и 5 — среди них строка возвращения с числом дней.
  const raw = pool[(Math.floor(n / 3) * 2 + (n % 3)) % pool.length]!;
  const num = (k: string) => vars[k as keyof typeof vars] ?? 0;
  return {
    text: raw
      .replace(/\{(\w+)\|([^|}]+)\|([^|}]+)\|([^|}]+)\}/g, (_, k: string, one: string, few: string, many: string) =>
        plural(num(k), one, few, many),
      )
      .replace(/\{(\w+)\}/g, (_, k: string) => String(num(k))),
    author: null,
  };
}

/** Число из строки «ключ + дата» — зерно, которое не меняется в пределах дня. */
export function coachSeed(...parts: string[]): number {
  let h = 0;
  for (const ch of parts.join("|")) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return Math.abs(h);
}
